import { useCollection } from "@/features/collections/useCollection";
import {
  CollectionToolbar,
  CollectionPagination,
} from "@/features/collections/CollectionControls";
import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { History } from "lucide-react";
import type {
  CoachingClip,
  CoachingNote,
  FeedbackBundle,
  FeedbackMatch,
  MatchSummary,
} from "@/lib/types";
import { FeedbackPlayerModal } from "@/features/playback/FeedbackPlayerModal";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  buildFeedbackReviewItems,
  type FeedbackReviewItem,
} from "./feedbackReviewItems";
import { shortDateLabel, summarySections } from "./feedbackHelpers";
import { buildPlayerResponseIndex } from "./responseSignals";
import type { PlayerResponseIndex } from "./responseSignals";
import { PlayerResponseChips } from "./components/PlayerResponseChips";
import { ReviewCardThumb } from "./FeedbackReviewTab";

/**
 * Feedback History.
 *
 * Grouped archive of everything the Review tab is intentionally
 * NOT pushing as the player's next action — reviewed notes, older
 * clips, and older match summaries.
 * Replaces the PR 1 thin wrapper.
 *
 * Approach: reuse the PR 2 normalized review item list. The "what
 * belongs here" rule is intentionally simple — anything not in the
 * Review tab's primary attention surface:
 *   1. Reviewed notes (`status === "reviewed"`, kind `note`).
 *   2. Reviewed clips or older clips beyond a 30-day recency cutoff.
 *   3. Older match summaries — summaries beyond the same cutoff.
 *
 * The 30-day cutoff is a pure UI threshold (no backend support
 * needed); the bundle is already filtered to the viewer's team /
 * scope server-side. Recently shared clips + summaries stay on the
 * Review tab so the player can act on them; older ones move here to
 * keep Review action-focused.
 *
 * Clips with review rows land in the Reviewed band immediately. Older
 * unreviewed clips still move here based on time so the Review feed stays
 * focused on current work.
 *
 * Optional filter pill row (All / Reviewed / Older) lets the
 * player narrow the archive when it grows.
 */

export type FeedbackHistoryTabProps = {
  bundle: FeedbackBundle | undefined;
  clubLogoUrl?: string | null;
  tenantTeamName?: string | null;
  reviewedNoteIds: Set<number>;
  /** PR 7: clip review state, so reviewed clips land in history. */
  reviewedClipIds?: Set<number>;
  /** Phase B: match-recap read state, so reviewed recaps land in the
   * Reviewed band of the history archive. */
  reviewedSummaryIds?: Set<number>;
};

/** Cutoff in days for "older" clips + summaries. UI-only — the
 * server already scopes the bundle to the viewer's team. */
const OLDER_THAN_DAYS = 30;

type HistoryFilter = "all" | "reviewed" | "older";

function historyToneLabel(tone: FeedbackReviewItem["tone"]): string {
  if (tone === "positive") return "Positive";
  if (tone === "correction") return "To work on";
  if (tone === "goal") return "Goal";
  if (tone === "team") return "Team concept";
  return "General";
}

export function FeedbackHistoryTab({
  bundle,
  clubLogoUrl,
  tenantTeamName,
  reviewedNoteIds,
  reviewedClipIds,
  reviewedSummaryIds,
}: FeedbackHistoryTabProps) {
  const matchesById = useMemo(
    () => new Map((bundle?.matches ?? []).map((match) => [match.id, match])),
    [bundle?.matches],
  );
  // The full normalized list — same source the Review feed uses.
  const all = useMemo(
    () =>
      buildFeedbackReviewItems({
        bundle,
        reviewedNoteIds,
        reviewedClipIds,
        reviewedSummaryIds,
      }),
    [bundle, reviewedNoteIds, reviewedClipIds, reviewedSummaryIds],
  );
  // PR 6 — player response signals (Got it / Need help / I tried this).
  const responseIndex = useMemo(
    () => buildPlayerResponseIndex(bundle?.reviews ?? []),
    [bundle?.reviews],
  );

  const [matchFilter, setMatchFilter] = useState("all");
  const [toneFilter, setToneFilter] = useState("all");

  // Apply the History filter: reviewed items keep, untracked clips +
  // summaries kept only if they're past the recency cutoff.
  const cutoffMs = useMemo(
    () => Date.now() - OLDER_THAN_DAYS * 24 * 60 * 60 * 1000,
    [],
  );
  const historyItems = useMemo(() => {
    return all.filter((it) => {
      if (it.status === "reviewed") return true;
      if (it.kind === "clip" || it.kind === "summary") {
        const ts = it.dateIso ? Date.parse(it.dateIso) : NaN;
        return Number.isFinite(ts) && ts < cutoffMs;
      }
      return false;
    });
  }, [all, cutoffMs]);

  const matchOptions = useMemo(
    () => [
      { value: "all", label: "All" },
      ...[...new Set(historyItems.flatMap((item) => item.matchLabel ?? []))]
        .sort((a, b) => a.localeCompare(b))
        .map((label) => ({ value: label, label })),
    ],
    [historyItems],
  );
  const toneOptions = useMemo(
    () => [
      { value: "all", label: "All" },
      ...[...new Set(historyItems.map((item) => item.tone))].map((tone) => ({
        value: tone,
        label: historyToneLabel(tone),
      })),
    ],
    [historyItems],
  );

  const collection = useCollection(historyItems, {
    scope: `family-history:${bundle?.players.map((p) => p.id).join(":")}`,
    text: (i) => `${i.title} ${i.subtitle} ${i.matchLabel ?? ""}`,
    matches: (i, f) =>
      f === "reviewed"
        ? i.status === "reviewed"
        : f === "older"
          ? i.status !== "reviewed"
          : true,
    predicate: (item) =>
      (matchFilter === "all" || item.matchLabel === matchFilter) &&
      (toneFilter === "all" || item.tone === toneFilter),
  });
  const filter = (collection.filter || "all") as HistoryFilter;
  const setFilter = (f: HistoryFilter) =>
    collection.setFilter(f === "all" ? "" : f);
  const reviewedItems = collection.items.filter((i) => i.status === "reviewed");
  const olderClips = collection.items.filter(
    (i) => i.kind === "clip" && i.status !== "reviewed",
  );
  const olderSummaries = collection.items.filter(
    (i) => i.kind === "summary" && i.status !== "reviewed",
  );
  const filteredCount = collection.filteredTotal;

  // Modal wiring — reuse the M5 player so a card opens in place.
  const [openNote, setOpenNote] = useState<CoachingNote | null>(null);
  const [openClip, setOpenClip] = useState<CoachingClip | null>(null);
  const [expandedSummaryId, setExpandedSummaryId] = useState<number | null>(
    null,
  );

  function openItem(item: FeedbackReviewItem) {
    if (item.source.kind === "note") setOpenNote(item.source.note);
    else if (item.source.kind === "clip") setOpenClip(item.source.clip);
    else if (item.source.kind === "summary") {
      const id = item.source.summary.id;
      setExpandedSummaryId((cur) => (cur === id ? null : id));
    }
  }

  if (historyItems.length === 0) {
    return (
      <div className="flex flex-col gap-6" data-testid="feedback-history">
        <header className="family-feedback-page-heading">
          <h2>Keep the useful moments.</h2>
          <p>Revisit reviewed feedback and older clips and recaps.</p>
        </header>
        <section
          className="family-history-empty"
          aria-labelledby="feedback-history-empty-heading"
          data-testid="feedback-history-empty"
        >
          <div className="family-feedback-state-icon" aria-hidden="true">
            <History />
          </div>
          <h3 id="feedback-history-empty-heading">
            Your reviewed moments will stay here.
          </h3>
          <p>
            Mark feedback done in Review and it will move into this archive,
            ready whenever you want to revisit it.
          </p>
          <Button asChild>
            <Link to="/feedback" search={{ tab: "review" }}>
              Go to Review
            </Link>
          </Button>
        </section>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="feedback-history">
      <header className="family-feedback-page-heading">
        <h2>Keep the useful moments.</h2>
        <p>Revisit reviewed feedback and older clips and recaps.</p>
      </header>
      {collection.controls ? (
        <HistoryFilterStrip
          active={filter}
          onSelect={setFilter}
          counts={{
            all: historyItems.length,
            reviewed: historyItems.filter((i) => i.status === "reviewed")
              .length,
            older: historyItems.filter((i) => i.status !== "reviewed").length,
          }}
        />
      ) : null}
      <CollectionToolbar
        collection={collection}
        label="history"
        searchPlaceholder="Search feedback, match or advice"
        extraFilters={[
          {
            label: "Match",
            ariaLabel: "Filter history by match",
            value: matchFilter,
            clearValue: "all",
            options: matchOptions,
            onChange: (value) => {
              setMatchFilter(value);
              collection.setPage(1);
            },
          },
          {
            label: "Tone",
            ariaLabel: "Filter history by tone",
            value: toneFilter,
            clearValue: "all",
            options: toneOptions,
            onChange: (value) => {
              setToneFilter(value);
              collection.setPage(1);
            },
          },
        ]}
      />

      {filteredCount === 0 ? (
        <p
          className="text-ink-muted text-sm px-3 py-6"
          data-testid="feedback-history-filter-empty"
        >
          Nothing in this filter. Try another chip above.
        </p>
      ) : (
        <div className="flex flex-col gap-8">
          {reviewedItems.length > 0 && filter !== "older" ? (
            <HistorySection
              heading="Reviewed"
              hideHeading
              testid="feedback-history-notes"
              items={reviewedItems}
              expandedSummaryId={expandedSummaryId}
              onOpen={openItem}
              responseIndex={responseIndex}
              matchesById={matchesById}
              clubLogoUrl={clubLogoUrl}
              tenantTeamName={tenantTeamName}
            />
          ) : null}
          {olderClips.length > 0 && filter !== "reviewed" ? (
            <HistorySection
              heading="Older clips"
              testid="feedback-history-clips"
              items={olderClips}
              expandedSummaryId={expandedSummaryId}
              onOpen={openItem}
              responseIndex={responseIndex}
              matchesById={matchesById}
              clubLogoUrl={clubLogoUrl}
              tenantTeamName={tenantTeamName}
            />
          ) : null}
          {olderSummaries.length > 0 && filter !== "reviewed" ? (
            <HistorySection
              heading="Older match recaps"
              testid="feedback-history-summaries"
              items={olderSummaries}
              expandedSummaryId={expandedSummaryId}
              onOpen={openItem}
              responseIndex={responseIndex}
              matchesById={matchesById}
              clubLogoUrl={clubLogoUrl}
              tenantTeamName={tenantTeamName}
            />
          ) : null}
        </div>
      )}

      <CollectionPagination collection={collection} />
      {openNote ? (
        <FeedbackPlayerModal
          target={{ kind: "note", note: openNote }}
          match={{ id: openNote.match_id ?? "", team_id: openNote.team_id }}
          onClose={() => setOpenNote(null)}
          players={bundle?.players}
          reviewedNoteIds={reviewedNoteIds}
          playerResponses={responseIndex}
        />
      ) : null}
      {openClip ? (
        <FeedbackPlayerModal
          target={{ kind: "clip", clip: openClip }}
          match={{ id: openClip.match_id ?? "", team_id: openClip.team_id }}
          onClose={() => setOpenClip(null)}
          players={bundle?.players}
          reviewedNoteIds={reviewedNoteIds}
          reviewedClipIds={reviewedClipIds}
          playerResponses={responseIndex}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filter strip
// ---------------------------------------------------------------------------

function HistoryFilterStrip({
  active,
  counts,
  onSelect,
}: {
  active: HistoryFilter;
  counts: Record<HistoryFilter, number>;
  onSelect: (f: HistoryFilter) => void;
}) {
  const FILTERS: ReadonlyArray<{ id: HistoryFilter; label: string }> = [
    { id: "all", label: "All" },
    { id: "reviewed", label: "Reviewed" },
    { id: "older", label: "Older" },
  ];
  return (
    <div
      role="tablist"
      aria-label="Filter history"
      data-testid="feedback-history-filters"
      className="family-feedback-filters flex gap-1.5 overflow-x-auto"
    >
      {FILTERS.map((f) => {
        const isActive = active === f.id;
        const count = counts[f.id];
        return (
          <button
            key={f.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            data-feedback-history-filter={f.id}
            data-active={isActive}
            onClick={() => onSelect(f.id)}
            className={[
              "shrink-0 rounded px-3 py-1 text-xs font-display font-semibold uppercase tracking-wide transition-colors",
              isActive
                ? "bg-accent text-accent-ink"
                : "border border-rule text-ink-muted hover:bg-field hover:text-ink",
            ].join(" ")}
          >
            {f.label}
            {count > 0 ? (
              <span
                className={[
                  "ml-1.5 text-[0.6rem] font-mono",
                  isActive ? "text-accent-ink" : "text-ink-muted",
                ].join(" ")}
              >
                {count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section + history card
// ---------------------------------------------------------------------------

function HistorySection({
  heading,
  hideHeading = false,
  testid,
  items,
  expandedSummaryId,
  onOpen,
  responseIndex,
  matchesById,
  clubLogoUrl,
  tenantTeamName,
}: {
  heading: string;
  hideHeading?: boolean;
  testid: string;
  items: FeedbackReviewItem[];
  expandedSummaryId: number | null;
  onOpen: (item: FeedbackReviewItem) => void;
  responseIndex: PlayerResponseIndex;
  matchesById: Map<string, FeedbackMatch>;
  clubLogoUrl?: string | null;
  tenantTeamName?: string | null;
}) {
  return (
    <section className="flex flex-col gap-3" data-testid={testid}>
      <h3 className={hideHeading ? "sr-only" : "font-display text-base text-ink"}>
        {heading}
      </h3>
      <ul className="flex flex-col gap-2">
        {items.map((item) => (
          <HistoryRow
            key={item.id}
            item={item}
            match={item.matchId ? matchesById.get(item.matchId) : undefined}
            clubLogoUrl={clubLogoUrl}
            tenantTeamName={tenantTeamName}
            expanded={
              item.source.kind === "summary" &&
              expandedSummaryId === item.source.summary.id
            }
            onOpen={() => onOpen(item)}
            responseIndex={responseIndex}
          />
        ))}
      </ul>
    </section>
  );
}

function HistoryRow({
  item,
  match,
  clubLogoUrl,
  tenantTeamName,
  expanded,
  onOpen,
  responseIndex,
}: {
  item: FeedbackReviewItem;
  match?: FeedbackMatch;
  clubLogoUrl?: string | null;
  tenantTeamName?: string | null;
  expanded: boolean;
  onOpen: () => void;
  responseIndex: PlayerResponseIndex;
}) {
  const dateLabel = item.dateIso ? shortDateLabel(item.dateIso) : "";
  const metaParts: string[] = [];
  if (item.matchLabel) metaParts.push(item.matchLabel);
  if (dateLabel && !item.matchLabel?.includes(dateLabel))
    metaParts.push(dateLabel);

  const actionLabel =
    item.kind === "summary"
      ? expanded
        ? "Hide recap"
        : "Read recap"
      : item.kind === "clip"
        ? "Watch clip"
        : "Open feedback";
  const response =
    item.source.kind === "note"
      ? responseIndex.forNote(item.source.note.id)
      : item.source.kind === "clip"
        ? responseIndex.forClip(item.source.clip.id)
        : null;
  const toneLabel =
    item.tone === "positive"
      ? "Positive"
      : item.tone === "correction"
        ? "To work on"
        : item.tone === "goal"
          ? "Goal"
          : item.tone === "team"
            ? "Team concept"
            : null;

  return (
    <li>
      <article
        data-testid="feedback-history-card"
        data-history-item-kind={item.kind}
        data-history-status={item.status}
        className="family-feedback-card flex flex-col gap-3 rounded border border-rule bg-surface p-3 sm:flex-row sm:items-start"
      >
        <ReviewCardThumb
          item={item}
          match={match}
          clubLogoUrl={clubLogoUrl}
          tenantTeamName={tenantTeamName}
        />
        <div className="family-feedback-card-copy min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" data-testid="feedback-history-card-type">
              {item.typeLabel}
            </Badge>
            {item.status === "reviewed" ? (
              <Badge
                variant="good"
                data-testid="feedback-history-card-reviewed-chip"
              >
                Done
              </Badge>
            ) : null}
            {toneLabel ? (
              <Badge variant={item.tone === "positive" ? "good" : "flag"}>
                {toneLabel}
              </Badge>
            ) : null}
          </div>
          <h3
            className="font-display text-ink"
            data-testid="feedback-history-card-title"
          >
            {item.title}
          </h3>
          {metaParts.length > 0 ? (
            <p
              className="text-ink-muted text-[0.7rem] font-mono"
              data-testid="feedback-history-card-meta"
            >
              {metaParts.join(" · ")}
            </p>
          ) : null}
          <p className="text-sm text-ink-muted">{item.subtitle}</p>
          {expanded && item.source.kind === "summary" ? (
            <SummaryExpanded summary={item.source.summary} />
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={onOpen}
              data-testid="feedback-history-card-action"
            >
              {actionLabel}
            </Button>
            {item.status === "reviewed" ? (
              <span className="text-xs text-ink-muted">Done</span>
            ) : null}
          </div>
          {response && item.source.kind === "note" ? (
            <PlayerResponseChips
              target={{ kind: "note", noteId: item.source.note.id }}
              currentSignal={response.signal}
              currentNote={response.note}
              acknowledgedAt={response.acknowledgedAt}
            />
          ) : response && item.source.kind === "clip" ? (
            <PlayerResponseChips
              target={{ kind: "clip", clipId: item.source.clip.id }}
              currentSignal={response.signal}
              currentNote={response.note}
              acknowledgedAt={response.acknowledgedAt}
            />
          ) : null}
        </div>
      </article>
    </li>
  );
}

function SummaryExpanded({ summary }: { summary: MatchSummary }) {
  const sections = summarySections(summary);
  if (sections.length === 0) return null;
  return (
    <div
      className="mt-3 flex flex-col gap-2 rounded border border-rule bg-surface p-3"
      data-testid="feedback-history-card-summary-expanded"
    >
      {sections.map(([label, value]) => (
        <div key={label}>
          <p className="text-[0.65rem] font-display uppercase tracking-wider text-ink-muted">
            {label}
          </p>
          <p className="text-sm text-ink mt-1">{value}</p>
        </div>
      ))}
    </div>
  );
}
